import asArray from 'utils/arrays/asArray.util';

/** Deep clone so edits do not mutate the service stored in Redux. */
export function cloneServiceForEdit(service) {
  const clone = JSON.parse(JSON.stringify(service));
  if (clone.tax_exempt_reason == null) {
    clone.tax_exempt_reason = '';
  }
  return clone;
}

export function buildServiceTaxSettingsPayload({ taxes, fees, isTaxExempt, taxExemptReason }) {
  return {
    taxes: asArray(taxes),
    fees: asArray(fees),
    is_tax_exempt: !!isTaxExempt,
    tax_exempt_reason: taxExemptReason == null ? '' : String(taxExemptReason),
  };
}

/** Prefer edited tax fields; fall back to server snapshot when edit state never loaded taxes. */
export function resolveTaxSettingsForSave(service, serverTaxSettings) {
  const server = serverTaxSettings ?? {};
  const hasEditedTaxIds = service?.tax_ids != null;
  const hasEditedFeeIds = service?.fee_ids != null;
  const isTaxExempt = service?.is_tax_exempt ?? server.is_tax_exempt ?? false;
  const taxExemptReason = isTaxExempt ? service?.tax_exempt_reason ?? server.tax_exempt_reason : service?.tax_exempt_reason ?? '';

  return buildServiceTaxSettingsPayload({
    taxes: hasEditedTaxIds ? service.tax_ids : server.taxes,
    fees: hasEditedFeeIds ? service.fee_ids : server.fees,
    isTaxExempt,
    taxExemptReason,
  });
}

export const normalizePrice = (price) => {
  if (price === '' || price == null) {
    return price;
  }
  const asNumber = Number(price);
  return Number.isFinite(asNumber) ? asNumber : price;
};

const roleExecutionPricesDiffer = (originalPrice, editedPrice) => normalizePrice(originalPrice) !== normalizePrice(editedPrice);

export function roleExecutionDetailsDiffer(editedRoleEx, originalRoleEx) {
  return roleExecutionPricesDiffer(originalRoleEx?.price, editedRoleEx?.price) || editedRoleEx?.duration !== originalRoleEx?.duration || editedRoleEx?.processing !== originalRoleEx?.processing;
}

/**
 * Match edited role executions to server ones by role_id.
 * Matched pairs are marked with role_id -1; changed pairs are returned in toPut.
 */
export function matchRoleExecutionsForUpdate(editedRoleExecutions, serverRoleExecutions) {
  const edited = asArray(editedRoleExecutions).map((roleEx) => ({ ...roleEx }));
  const server = asArray(serverRoleExecutions).map((roleEx) => ({ ...roleEx }));
  const toPut = [];

  edited.forEach((editedRoleEx) => {
    const matchIndex = server.findIndex((serverRoleEx) => serverRoleEx.role_id !== -1 && serverRoleEx.role_id === editedRoleEx.role_id);
    if (matchIndex === -1) {
      return;
    }

    const serverRoleEx = server[matchIndex];
    if (roleExecutionDetailsDiffer(editedRoleEx, serverRoleEx)) {
      toPut.push({ ...editedRoleEx });
    }

    editedRoleEx.role_id = -1;
    server[matchIndex].role_id = -1;
  });

  return { editedRoleExecutions: edited, serverRoleExecutions: server, toPut };
}

/**
 * Role execution ids whose price changed vs the snapshot taken when edit opened.
 * @param {Object} editedService
 * @param {Object} originalService - frozen clone from edit open (not the live Redux prop)
 */
export function calculateDifferentRoleExecutions(editedService, originalService) {
  const differenceIds = [];
  const seen = new Set();

  asArray(editedService?.role_executions).forEach((roleEx) => {
    const originalRoleEx = asArray(originalService?.role_executions).find((ex) => ex.role_id === roleEx.role_id);
    if (!originalRoleEx || !roleExecutionPricesDiffer(originalRoleEx.price, roleEx.price)) {
      return;
    }

    const executionId = roleEx.id ?? originalRoleEx.id;
    if (executionId && !seen.has(executionId)) {
      seen.add(executionId);
      differenceIds.push(executionId);
    }
  });

  return differenceIds;
}

export function sumUpFutureAppointments(futureAppoinmentsResponse) {
  const sums = {
    custom_priced: 0,
    default_priced: 0,
  };

  const results = Array.isArray(futureAppoinmentsResponse) ? futureAppoinmentsResponse : futureAppoinmentsResponse ? [futureAppoinmentsResponse] : [];

  results.forEach((result) => {
    sums.custom_priced += result?.custom_priced ?? 0;
    sums.default_priced += result?.default_priced ?? 0;
  });

  return sums;
}
